import React, { useEffect, useRef, useState } from 'react';
import { DailiesAuthor, DailyData, Maintenance } from '../room/types';
import { CardTitle } from './CardTitle';
import {
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tooltipClasses,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MobileDisclaimer } from '../../dialog/MobileDisclaimer';
import { isMobile } from 'react-device-detect';
import { Rankings } from './Rankings';
import { FaDiscord, FaXTwitter } from 'react-icons/fa6';
import { DailiesButton } from '../../common/components/buttons/DailiesButton';
import { ImageDisplay } from '../../dialog/ImageDisplay';
import { Announcement } from '../../dialog/Announcement';
import { fetchGet, fetchPost } from '@par/shared/api/common';
import {
  fetchGamesPlayedCount,
  fetchRankings,
} from '@par/shared/components/main';
import { SearchPoolData } from '../../common/components/SearchPoolStackedBar';
import { SearchPoolAbout } from '../../dialog/SearchPoolAbout';
import { Diversity3, SelfImprovement } from '@mui/icons-material';
import { UpcomingEvents } from './UpcomingEvents';
import { DemonButton } from '../../common/components/demon-button/DemonButton';
import {
  DemonsData,
  vanquishProgress,
} from '../../dialog/FaceYourDemonsDialog';
import { QuickPlaySoloDialog } from '../../dialog/QuickPlaySoloDialog';
import { CreateGameConfig, Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { QuickPlayMatchDialog } from '../../dialog/QuickPlayMatchDialog';
import { PlayersAroundTheWorldDialog } from '../../dialog/PlayersAroundTheWorldDialog';
import { PlayersAroundTheWorld } from '../leaderboards/PlayersAroundTheWorld';
import { HelpfulTipsDialog } from '../../dialog/HelpfulTipsDialog';
import { MobileBrowserDailyData } from '@par/shared/types/mobile-daily';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocalMoviesIcon from '@mui/icons-material/LocalMovies';
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball';
import SchoolIcon from '@mui/icons-material/School';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import { BsFillHouseHeartFill } from 'react-icons/bs';
import HelpIcon from '@mui/icons-material/Help';
import { StyledPaperItem } from '../../common/mui-components';
import { GiRank3 } from 'react-icons/gi';
import { WhosOnline } from '@par/shared/common/types';
import { BiGhost } from 'react-icons/bi';
import { GiDevilMask } from 'react-icons/gi';
import { FireEffect } from '../../common/components/fire-effect/FireEffect';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { SearchBarMain } from '../header/SearchBarMain';

const NeonButton = styled(Button)(({ theme }) => ({
  boxShadow: '0 1px 2px 0 rgba(255, 255, 255, 0.26)',
}));

const NeonButtonText = styled(Typography)(({ theme }) => ({
  color: '#fff',
  textTransform: 'capitalize',
}));

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
}));

type Topic = 'moviesAndShows' | 'sports' | 'wildcard' | 'lifestyle' | 'scholar';

const TOPICS: { topic: Topic; category: string; icon: any }[] = [
  {
    topic: 'moviesAndShows',
    category: null,
    icon: <LocalMoviesIcon />,
  },
  {
    topic: 'sports',
    category: null,
    icon: <SportsBaseballIcon />,
  },
  {
    topic: 'wildcard',
    category: null,
    icon: <PsychologyAltIcon />,
  },
  {
    topic: 'lifestyle',
    category: null,
    icon: <BsFillHouseHeartFill fontSize={24} />,
  },
  {
    topic: 'scholar',
    category: null,
    icon: <SchoolIcon />,
  },
];

type PropTypes = {
  connected?: boolean;
  dailiesAuthor?: DailiesAuthor;
  dailyData: DailyData;
  demonsData: DemonsData;
  demonGamePlayedToday?: boolean;
  handleDailyCountdownStop: () => void;
  handleDemonButtonClick: () => void;
  handleGhostChallengesView: () => void;
  handlePlayDailyClick: (currentDailySet: number) => void;
  handlePlayPracticeGameClick: () => void;
  handleQuickPlay: (isMatchMaking?: boolean) => void;
  incomingGhostChallenges: number;
  lastDailySetPlayedCache: number;
  lobbyGames?: Game[];
  maintenance: Maintenance;
  mobileDailyData: MobileBrowserDailyData;
  onlinePlayer: WhosOnline;
  onUpdateProfile: () => void;
  profile: Profile;
  setCreateGameConfig: (opts: CreateGameConfig) => void;
  testingFeature: boolean;
};

export const Main = ({
  connected,
  dailiesAuthor,
  dailyData,
  demonsData,
  demonGamePlayedToday,
  handleDailyCountdownStop,
  handleDemonButtonClick,
  handleGhostChallengesView,
  handlePlayDailyClick,
  handlePlayPracticeGameClick,
  incomingGhostChallenges,
  lastDailySetPlayedCache,
  lobbyGames,
  maintenance,
  mobileDailyData,
  handleQuickPlay,
  onlinePlayer,
  onUpdateProfile,
  profile,
  setCreateGameConfig,
  testingFeature,
}: PropTypes) => {
  const navigate = useNavigate();
  const [rankings, setRankings] = useState<any>();
  const [openGames, setOpenGames] = useState<Game[]>();
  const [userRank, setUserRank] = useState<{
    rank: number;
  }>();
  const [openAnnouncement, setOpenAnnouncement] = useState(false);
  const [openMobileDisclaimer, setOpenMobileDisclaimer] = useState(false);
  const [openPoster, setOpenPoster] = useState(false);
  const [openPlaySolo, setOpenPlaySolo] = useState(false);
  const [openPlayMatch, setOpenPlayMatch] = useState(false);
  const [totalGamesPlayed, setTotalGamesPlayed] = useState<{
    gamesPlayedPastHour: number;
    gamesPlayedPast24Hours: number;
    gamesPlayedAllTime: number;
  }>();
  const [searchPoolData, setSearchPoolData] = useState<SearchPoolData>();
  const [totalMatches, setTotalMatches] = useState<number>();
  const [openSearchAboutDialog, setOpenSearchAboutDialog] = useState(false);
  const [userLocations, setUserLocations] = useState<any>();
  const [openWorldMap, setOpenWorldMap] = useState(false);
  const [openHelpfulTips, setOpenHelpfulTips] = useState(false);
  const [openDailiesHelp, setOpenDailiesHelp] = useState(false);

  const theme = useTheme();
  const gridContainerRightRef = useRef<any>(null);
  const gridContainerProfileRef = useRef<any>(null);
  const last14Days = new Date(new Date().getTime() - 14 * 24 * 60 * 60 * 1000);
  const {
    rankedRating,
    rankedMatchWins,
    rankedMatchLosses,
    rankedMatchTies,
    rankedSitAndGoGamesPlayed,
  } = profile;

  const totalRankedGamesPlayed =
    (rankedMatchWins || 0) +
    (rankedMatchLosses || 0) +
    (rankedMatchTies || 0) +
    (rankedSitAndGoGamesPlayed || 0);

  useEffect(() => {
    getRankings();
    getTotalGamesPlayed();
    fetchLocations();
    fetchUserRankings();
    fetchGamesPlayedCount();
    fetchGlobalSearchStats();
    if (!profile.announcementSeen) {
      setOpenAnnouncement(true);
      // setOpenSearchAboutDialog(true);
    }
    if (isMobile && !profile.mobileDisclaimerSeen)
      setOpenMobileDisclaimer(true);
  }, []);

  const getRankings = async () => setRankings(await fetchRankings());

  const getTotalGamesPlayed = async () =>
    setTotalGamesPlayed(await fetchGamesPlayedCount());

  const fetchReferralCodes = async () => {
    await fetchGet(`/profile/referral-codes`);
    onUpdateProfile();
  };

  const fetchLocations = async () => {
    const res = await fetchGet('/users/locations');
    setUserLocations(res);
  };

  useEffect(() => {
    if (!lobbyGames) return;
    const filtered = lobbyGames.filter(
      (g) =>
        !g.gameOver &&
        !g.gameStarted &&
        g.status !== 'Starting' &&
        g.mode !== 'solo'
    );
    setOpenGames(filtered);
  }, [lobbyGames]);

  const handleProfileCardClick = (profileId: string) =>
    navigate(`/profile/overview/${profileId}`);

  const fetchUserRankings = async () => {
    const res = await fetchGet(`/rankings/profile/${profile.id}`);
    setUserRank(res);
  };

  const handleMmrClick = () => navigate('/leaderboards');

  const handleCloseSearchAboutDialog = async () => {
    setOpenSearchAboutDialog(false);
    if (!profile) return;
    await fetchPost('/profile/announcement-seen', {
      profileId: profile.id,
    });
    onUpdateProfile();
  };

  const handleCloseAnnouncement = async () => {
    setOpenAnnouncement(false);
    if (!profile) return;
    await fetchPost('/profile/announcement-seen', {
      profileId: profile.id,
    });
    onUpdateProfile();
  };

  const closeMobileDisclaimer = async () => {
    setOpenMobileDisclaimer(false);
    if (!profile) return;
    await fetchPost('/profile/mobile-disclaimer-seen', {
      profileId: profile.id,
    });
    onUpdateProfile();
  };

  const handleTourneyParticipationClick = async () => {
    setOpenAnnouncement(false);
    if (!profile) return;
    await fetchPost('/profile/beta-tourney', { profileId: profile.id });
    onUpdateProfile();
  };

  const fetchGlobalSearchStats = async () => {
    const res = await fetchGet(`/match-making/global-search-stats/true`);
    setSearchPoolData(res);
    setTotalMatches(
      res.matches1200 +
        res.matches12001500 +
        res.matches15001800 +
        res.matches1800
    );
  };

  const getPlayerTotalGamesPlayed = () =>
    (profile.totalSoloGamesPlayed || 0) +
    (profile.totalMatchGamesPlayed || 0) +
    (profile.totalSitAndGoGamesPlayed || 0);

  const handleDailiesClick = () => {
    window.open('https://mobile.par.live', '_blank');
  };

  const dailiesTooltip = (
    <StyledPaperItem>
      <Stack spacing={1}>
        <Typography variant="subtitle1">
          Dailies is best played on mobile but also available on desktop
        </Typography>
        <Typography variant="subtitle1">
          Dailies features a buzzer format which differs from standard desktop
          games
        </Typography>
        <Typography variant="subtitle1">
          Click here or go to <strong>mobile.par.live</strong> on your mobile
          device
        </Typography>
      </Stack>
    </StyledPaperItem>
  );

  return (
    <>
      {/* <PlaySoloDialog
        open={openPlaySolo}
        close={() => setOpenPlaySolo(false)}
        profile={profile}
      /> */}
      {/* <QuickPlaySoloDialog
        open={openPlaySolo}
        close={() => setOpenPlaySolo(false)}
        handlePlayPracticeGameClick={handlePlayPracticeGameClick}
        handleQuickPlay={handleQuickPlay}
        maintenance={maintenance}
        profile={profile}
        setCreateGameConfig={setCreateGameConfig}
      />
      <QuickPlayMatchDialog
        open={openPlayMatch}
        close={() => setOpenPlayMatch(false)}
        handleQuickPlay={handleQuickPlay}
        maintenance={maintenance}
        profile={profile}
        setCreateGameConfig={setCreateGameConfig}
      /> */}
      <MobileDisclaimer
        closeMobileDisclaimer={closeMobileDisclaimer}
        openMobileDisclaimer={openMobileDisclaimer}
      ></MobileDisclaimer>
      <ImageDisplay
        closeImageDisplay={() => setOpenPoster(false)}
        imgSource="https://res.cloudinary.com/dqlqwdqs9/image/upload/v1702072966/Beta_Tourney_7_oquyi2.png"
        openImageDisplay={openPoster}
      ></ImageDisplay>
      {profile ? (
        <Announcement
          closeAnnouncement={handleCloseAnnouncement}
          openAnnouncement={openAnnouncement}
        ></Announcement>
      ) : null}
      <SearchPoolAbout
        close={handleCloseSearchAboutDialog}
        open={openSearchAboutDialog}
      />
      <PlayersAroundTheWorldDialog
        close={() => setOpenWorldMap(false)}
        open={openWorldMap}
        userLocations={userLocations}
      />
      <HelpfulTipsDialog
        open={openHelpfulTips}
        close={() => setOpenHelpfulTips(false)}
      />
      <Container sx={{ mt: 6, paddingBottom: 10 }}>
        <Grid container spacing={3}>
          {/* <Grid item xs={12} sm={12}>
            <Box className="snow">
              <div></div>
              <Button fullWidth onClick={() => setOpenPoster(true)}>
                <Stack
                  direction={'row'}
                  spacing={2}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#169a16',
                      fontWeight: 600,
                    }}
                  >
                    Beta Tournament #7 on Dec 12th, 9PM ET.
                  </Typography>
                  <Typography
                    sx={{
                      color: '#169a16',
                      fontWeight: 600,
                    }}
                  >
                    Join us on
                    <Link
                      sx={{ cursor: 'pointer', marginLeft: 2, marginRight: 2 }}
                      href="https://discord.gg/upX48KPfHd"
                      target="_blank"
                    >
                      Discord
                    </Link>
                  </Typography>
                </Stack>
              </Button>
            </Box>
          </Grid> */}
          <Grid item xs={12} md={8} ref={gridContainerRightRef}>
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                sm={6}
                className="Profile"
                ref={gridContainerProfileRef}
              >
                <Card
                  sx={{
                    boxShadow: `${theme.palette.primary.main} 0px 0px 4px -1px, ${theme.palette.primary.main} 0px 0px 5px 0px, ${theme.palette.primary.main} 0px 0px 10px 0px;`,
                    height: '100%',
                    padding: 0.5,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent={'space-between'}
                    alignItems="center"
                    width="100%"
                  >
                    <CardActionArea
                      sx={{
                        borderRight: '1px solid',
                        borderColor: theme.palette.grey[600],
                      }}
                    >
                      <Stack
                        onClick={() => handleProfileCardClick(profile.id)}
                        direction="row"
                        spacing={2}
                        alignItems="center"
                      >
                        <Box width={'100px'}>
                          <CardMedia
                            component="img"
                            image={profile.avatarUrl}
                            alt="profile picture"
                            sx={{
                              height: '100px',
                              width: '100px',
                              borderRadius: '8px',
                            }}
                          />
                        </Box>
                        <Stack spacing={0.5} justifyContent={'left'}>
                          <Typography textAlign={'left'} fontWeight={600}>
                            {profile.username}
                          </Typography>
                          {onlinePlayer?.online && !onlinePlayer?.isIdle ? (
                            <Typography
                              variant="subtitle2"
                              textAlign={'left'}
                              color="success.main"
                            >
                              Online
                            </Typography>
                          ) : onlinePlayer?.online && onlinePlayer?.isIdle ? (
                            <Typography
                              textAlign={'left'}
                              variant="subtitle2"
                              color="info.main"
                            >
                              Idle
                            </Typography>
                          ) : (
                            <Typography
                              variant="subtitle2"
                              textAlign={'left'}
                              color={theme.palette.grey[500]}
                            >
                              Offline
                            </Typography>
                          )}
                          <Stack direction={'row'} spacing={3}>
                            <Stack
                              direction={'row'}
                              spacing={1}
                              alignItems={'center'}
                            >
                              <Box
                                display={'flex'}
                                alignItems={'center'}
                                sx={{
                                  color: theme.palette.ranked.main,
                                  fontSize: 20,
                                }}
                              >
                                <GiRank3 />
                              </Box>
                              {profile.calibrated ? (
                                <Typography
                                  variant="subtitle1"
                                  color={theme.palette.ranked.main}
                                  fontWeight={600}
                                >
                                  {rankedRating}
                                </Typography>
                              ) : (
                                <Tooltip
                                  title={`${
                                    10 - totalRankedGamesPlayed
                                  } calibration games remaining`}
                                  placement="top"
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontStyle={'italic'}
                                    color={theme.palette.grey[500]}
                                  >
                                    --
                                  </Typography>
                                </Tooltip>
                              )}
                            </Stack>
                            {userRank?.rank ? (
                              <Stack
                                direction={'row'}
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="subtitle1">
                                  Rank
                                </Typography>
                                {profile.lastRankedGamePlayedAt >
                                last14Days.getTime() ? (
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    #{userRank?.rank}
                                  </Typography>
                                ) : (
                                  <Tooltip
                                    title="You must play a ranked game within the past two weeks to qualify for the leaderboard"
                                    placement="top"
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      fontStyle={'italic'}
                                      color={theme.palette.grey[500]}
                                    >
                                      --
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Stack>
                            ) : null}
                          </Stack>
                        </Stack>
                      </Stack>
                    </CardActionArea>
                    <Stack justifyContent={'space-between'} pl={0.5}>
                      <IconButton
                        color="primary"
                        onClick={handleGhostChallengesView}
                      >
                        <Badge
                          badgeContent={incomingGhostChallenges}
                          color="warning"
                        >
                          <BiGhost />
                        </Badge>
                      </IconButton>
                      {getPlayerTotalGamesPlayed() >= 100 ? (
                        <Box
                          sx={{
                            position: 'relative',
                          }}
                        >
                          <IconButton
                            onClick={handleDemonButtonClick}
                            sx={{
                              fontSize: 26,
                              color: theme.palette.demon.contrastText,
                              zIndex: 1,
                            }}
                          >
                            <GiDevilMask
                              style={{
                                stroke: theme.palette.demon.main,
                                strokeWidth: 10,
                              }}
                            />
                          </IconButton>
                          {!demonGamePlayedToday ? <FireEffect small /> : null}
                        </Box>
                      ) : null}
                    </Stack>
                  </Stack>
                </Card>
                {/* <Card
                  onClick={() => handleProfileCardClick(profile.id)}
                  sx={{
                    boxShadow: `${theme.palette.primary.main} 0px 0px 4px -1px, ${theme.palette.primary.main} 0px 0px 5px 0px, ${theme.palette.primary.main} 0px 0px 10px 0px;`,
                    height: '100%',
                  }}
                >
                  <CardActionArea
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <CardMedia
                        component="img"
                        image={profile.avatarUrl}
                        alt="profile picture"
                        sx={{
                          width: '70%',
                          height: '200px',
                          borderRadius: '8px',
                          margin: '16px',
                          marginRight: '-16px',
                          // maxHeight: '200px',
                        }}
                      />
                      <Stack spacing={1} sx={{ marginY: 1, marginRight: 1 }}>
                        <Paper
                          elevation={12}
                          sx={{ paddingX: 1, width: '100px' }}
                        >
                          <Stack alignItems="center">
                            <Typography
                              variant="overline"
                              color={theme.palette.ranked.main}
                            >
                              MMR
                            </Typography>
                            {profile.calibrated ? (
                              <Typography
                                gutterBottom
                                color={theme.palette.ranked.main}
                              >
                                {rankedRating}
                              </Typography>
                            ) : (
                              <Tooltip
                                title={`${
                                  10 - totalRankedGamesPlayed
                                } calibration games remaining`}
                                placement="top"
                              >
                                <Typography
                                  gutterBottom
                                  variant="body2"
                                  fontStyle={'italic'}
                                  color={theme.palette.grey[500]}
                                >
                                  calibrating
                                </Typography>
                              </Tooltip>
                            )}
                          </Stack>
                        </Paper>
                        {userRank?.rank ? (
                          <Paper
                            elevation={6}
                            sx={{ paddingX: 1, width: '100px' }}
                          >
                            <Stack alignItems="center">
                              <Typography variant="overline">
                                My Rank
                              </Typography>
                              {profile.lastRankedGamePlayedAt >
                              last14Days.getTime() ? (
                                <Typography gutterBottom>
                                  #{userRank?.rank}
                                </Typography>
                              ) : (
                                <Tooltip
                                  title="You must play a ranked game within the past two weeks to qualify for the leaderboard"
                                  placement="top"
                                >
                                  <Typography
                                    gutterBottom
                                    color={theme.palette.grey[500]}
                                    textTransform={'capitalize'}
                                  >
                                    inactive
                                  </Typography>
                                </Tooltip>
                              )}
                            </Stack>
                          </Paper>
                        ) : null}
                      </Stack>
                    </Stack>
                  </CardActionArea>
                </Card> */}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack
                  alignItems={'baseline'}
                  justifyContent={'center'}
                  flex={1}
                >
                  <Tooltip
                    title={`1 hr: ${totalGamesPlayed?.gamesPlayedPastHour} | 24 hrs: ${totalGamesPlayed?.gamesPlayedPast24Hours}`}
                    placement="top"
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent={'center'}
                      alignItems="center"
                      width="100%"
                      mb={1}
                    >
                      <Typography textAlign={'center'} fontWeight={600}>
                        {totalGamesPlayed?.gamesPlayedAllTime}
                      </Typography>
                      <Typography textAlign={'center'}>games played</Typography>
                    </Stack>
                  </Tooltip>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent={'center'}
                    alignItems="center"
                    width="100%"
                  >
                    <IconButton
                      color="primary"
                      href="https://discord.gg/upX48KPfHd"
                      target="_blank"
                      sx={{ mr: 1, fontSize: 20 }}
                    >
                      <FaDiscord />
                    </IconButton>
                    <Typography textAlign={'center'}>Join our</Typography>
                    <Link href="https://discord.gg/upX48KPfHd" target="_blank">
                      Discord
                    </Link>
                    !
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent={'center'}
                    alignItems="center"
                    width="100%"
                  >
                    <IconButton
                      color="primary"
                      href="https://twitter.com/ParTrivia"
                      target="_blank"
                      sx={{ mr: 1, fontSize: 20 }}
                    >
                      <FaXTwitter />
                    </IconButton>
                    <span>Follow our</span> &nbsp;
                    <Link href="https://twitter.com/ParTrivia" target="_blank">
                      Twitter
                    </Link>
                    !
                  </Stack>
                </Stack>
                {/* <Card
                    sx={{
                      boxShadow: `${theme.palette.primary.main} 0px 0px 4px -1px, ${theme.palette.primary.main} 0px 0px 5px 0px, ${theme.palette.primary.main} 0px 0px 10px 0px;`,
                      width: '100%',
                    }}
                  >
                    <CardContent>
                      <Tooltip
                        title='This is the "true count" which includes total players in match and sng games'
                        placement="top"
                      >
                        <Typography textAlign={'center'} mb={1}>
                          Games played in the past:
                        </Typography>
                      </Tooltip>
                      <Stack
                        direction="row"
                        spacing={{ xs: 1, sm: 2, md: 4 }}
                        divider={<Divider orientation="vertical" flexItem />}
                        sx={{
                          marginTop: 2,
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Stack>
                          <Typography textAlign={'center'} mb={1}>
                            hour
                          </Typography>
                          <Typography
                            textAlign={'center'}
                            variant="h4"
                            color={
                              theme.palette.mode === 'light'
                                ? theme.palette.dailies.main
                                : theme.palette.text.primary
                            }
                            sx={{
                              textShadow:
                                theme.palette.mode === 'dark'
                                  ? `0 0 0px ${theme.palette.dailies.main}, 0 0 0px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                                  : 'none',
                            }}
                          >
                            {totalGamesPlayed?.gamesPlayedPastHour}
                          </Typography>
                        </Stack>
                        <Stack>
                          <Typography textAlign={'center'} mb={1}>
                            24 hrs
                          </Typography>
                          <Typography
                            textAlign={'center'}
                            variant="h4"
                            color={
                              theme.palette.mode === 'light'
                                ? theme.palette.dailies.main
                                : theme.palette.text.primary
                            }
                            sx={{
                              textShadow:
                                theme.palette.mode === 'dark'
                                  ? `0 0 0px ${theme.palette.dailies.main}, 0 0 0px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                                  : 'none',
                            }}
                          >
                            {totalGamesPlayed?.gamesPlayedPast24Hours}
                          </Typography>
                        </Stack>
                        <Stack>
                          <Tooltip title="Since Jan 3rd, 2023" placement="top">
                            <Typography textAlign={'center'} mb={1}>
                              all time
                            </Typography>
                          </Tooltip>
                          <Typography
                            textAlign={'center'}
                            variant="h4"
                            color={
                              theme.palette.mode === 'light'
                                ? theme.palette.dailies.main
                                : theme.palette.text.primary
                            }
                            sx={{
                              textShadow:
                                theme.palette.mode === 'dark'
                                  ? `0 0 0px ${theme.palette.dailies.main}, 0 0 0px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                                  : 'none',
                            }}
                          >
                            {totalGamesPlayed?.gamesPlayedAllTime}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card> */}
              </Grid>
              <Grid item xs={12} sm={12} className="Leaderboard">
                <Card
                  sx={{
                    boxShadow: `${theme.palette.primary.main} 0px 0px 4px -1px, ${theme.palette.primary.main} 0px 0px 5px 0px, ${theme.palette.primary.main} 0px 0px 10px 0px;`,
                    minHeight: '500px',
                    height: '100%',
                  }}
                >
                  <Link
                    onClick={handleMmrClick}
                    sx={{ cursor: 'pointer', marginBottom: 2 }}
                  >
                    <CardTitle text="Top 20 Leaderboard:"></CardTitle>
                  </Link>
                  <CardContent>
                    <Rankings rankings={rankings}></Rankings>
                    <Typography
                      variant="caption"
                      display={'flex'}
                      justifyContent={'right'}
                      mt={1}
                      color="primary.main"
                    >
                      * Must have played a ranked game in the past two weeks
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack height={'100%'} spacing={4}>
              {/* <Card
                sx={{
                  boxShadow: `${theme.palette.primary.main} 0px 0px 4px -1px, ${theme.palette.primary.main} 0px 0px 5px 0px, ${theme.palette.primary.main} 0px 0px 10px 0px;`,
                  paddingX: 2,
                  paddingBottom: 2,
                }}
              >
                <CardTitle text="Play a Game"></CardTitle>
                <Stack spacing={2}>
                  <Stack
                    direction={'row'}
                    width={'100%'}
                    height={100}
                    spacing={2}
                  >
                    <NeonButton
                      variant="outlined"
                      fullWidth
                      onClick={() => setOpenPlaySolo(true)}
                    >
                      <NeonButtonText variant="h5led">Solo</NeonButtonText>
                      <SelfImprovement
                        sx={{
                          color: '#cda68c1c',
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    </NeonButton>
                    <NeonButton
                      variant="outlined"
                      fullWidth
                      onClick={() => setOpenPlayMatch(true)}
                    >
                      <NeonButtonText variant="h5led">Match</NeonButtonText>
                      <Diversity3
                        sx={{
                          color: '#cda68c1c',
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    </NeonButton>
                  </Stack>
                </Stack>
              </Card> */}
              {/* <DailiesButton
                connected={connected}
                dailiesAuthor={dailiesAuthor}
                dailyData={dailyData}
                handleDailyCountdownStop={handleDailyCountdownStop}
                handlePlayDailyClick={handlePlayDailyClick}
                lastDailySetPlayedCache={lastDailySetPlayedCache}
                maintenance={maintenance}
                profile={profile}
              /> */}
              {/* 
              {getPlayerTotalGamesPlayed() >= 100 ? (
                <DemonButton
                  demonGamePlayedToday={demonGamePlayedToday}
                  handleDemonButtonClick={handleDemonButtonClick}
                  label={
                    !demonsData?.demons?.length &&
                    !demonsData?.vanquished?.length
                      ? 'No demons, yet'
                      : vanquishProgress(demonsData) === 100
                      ? `Demons Vanquished`
                      : 'Face Your Demons'
                  }
                  hideFire={
                    vanquishProgress(demonsData) === 100 ||
                    (!demonsData?.demons?.length &&
                      !demonsData?.vanquished?.length)
                  }
                  nextDemonSchedule={dailyData.nextDailySchedule}
                />
              ) : null} */}

              {/* <Card
                sx={{
                  height: '50%',
                  boxShadow: `${theme.palette.primary.main} 0px 0px 4px -1px, ${theme.palette.primary.main} 0px 0px 5px 0px, ${theme.palette.primary.main} 0px 0px 10px 0px;`,
                }}
              >
                <CardTitle text="Upcoming Events"></CardTitle>
                <CardContent>
                  <UpcomingEvents />
                </CardContent>
              </Card> */}
              {/* <Card
                onClick={handleDailiesClick}
                sx={{
                  boxShadow: `${theme.palette.primary.main} 0px 0px 4px -1px, ${theme.palette.primary.main} 0px 0px 5px 0px, ${theme.palette.primary.main} 0px 0px 10px 0px;`,
                  height: '100%',
                  width: '100%',
                }}
              >
                <CardActionArea
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                  }}
                >
                  <StyledTooltip title={dailiesTooltip} placement="top">
                    <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
                      <HelpIcon color="primary" />
                    </Box>
                  </StyledTooltip>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                    }}
                  >
                    <OpenInNewIcon color="primary" />
                  </Box>
                  <CardTitle text="Dailies"></CardTitle>
                  <CardContent>
                    <Typography fontWeight={600} mb={2} textAlign={'center'}>
                      Today's Categories
                    </Typography>
                    {mobileDailyData ? (
                      <Stack spacing={1}>
                        <Stack direction={'row'} spacing={1}>
                          <Box>
                            {
                              TOPICS.find((t) => t.topic === 'moviesAndShows')
                                .icon
                            }
                          </Box>
                          <Typography>
                            {mobileDailyData.topics['moviesAndShows']}
                          </Typography>
                        </Stack>
                        <Stack direction={'row'} spacing={1}>
                          <Box>
                            {TOPICS.find((t) => t.topic === 'sports').icon}
                          </Box>
                          <Typography>
                            {mobileDailyData.topics['sports']}
                          </Typography>
                        </Stack>
                        <Stack direction={'row'} spacing={1}>
                          <Box>
                            {TOPICS.find((t) => t.topic === 'wildcard').icon}
                          </Box>
                          <Typography>
                            {mobileDailyData.topics['wildcard']}
                          </Typography>
                        </Stack>
                        <Stack direction={'row'} spacing={1}>
                          <Box>
                            {TOPICS.find((t) => t.topic === 'lifestyle').icon}
                          </Box>
                          <Typography>
                            {mobileDailyData.topics['lifestyle']}
                          </Typography>
                        </Stack>
                        <Stack direction={'row'} spacing={1}>
                          <Box>
                            {TOPICS.find((t) => t.topic === 'scholar').icon}
                          </Box>
                          <Typography>
                            {mobileDailyData.topics['scholar']}
                          </Typography>
                        </Stack>
                      </Stack>
                    ) : null}
                  </CardContent>
                </CardActionArea>
              </Card> */}
              <Button
                variant="outlined"
                size="large"
                onClick={() => setOpenHelpfulTips(true)}
              >
                New to Par? Tips & Tricks
              </Button>
              <Card
                sx={{
                  position: 'relative',
                  height: '100%',
                  boxShadow: `${theme.palette.primary.main} 0px 0px 4px -1px, ${theme.palette.primary.main} 0px 0px 5px 0px, ${theme.palette.primary.main} 0px 0px 10px 0px;`,
                }}
              >
                <CardTitle text="Player Locations"></CardTitle>
                <IconButton
                  onClick={() => setOpenWorldMap(true)}
                  color="primary"
                  sx={{ position: 'absolute', top: 5, right: 5 }}
                >
                  <OpenInFullIcon />
                </IconButton>
                <Stack>
                  <PlayersAroundTheWorld
                    customCenter={[0, 20]}
                    userLocations={userLocations}
                  />
                </Stack>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
